import type {
  BackgroundJobSummary,
  ExportDependencyScope,
  ExportMediaMode,
  ExportScope,
  ExportVersionMode
} from '../../../shared/background-jobs';
import type { ExportLanguageProfile, RecipeExportFormat } from '../../../shared/exports';
import type { MenuExportFormat } from '../../../shared/menu-production';
import type { ScopePdfDocumentPurpose } from '../../../shared/scope-pdf';
import type { RecipeScaleRequest } from '../../../shared/domain';
import { jsonBody, requestJson } from '../../shared/api-client';
import type { RecipeExportKind } from './api';
import { notifyTasksChanged } from '../../tasks/task-events';

export interface EnqueuedJobResponse {
  job: BackgroundJobSummary;
  reused?: boolean;
}

async function taskAware<T>(request: Promise<T>): Promise<T> {
  const result = await request;
  notifyTasksChanged();
  return result;
}

export const exportJobApi = {
  list: (limit = 100) => requestJson<{ jobs: BackgroundJobSummary[] }>(`/api/jobs/exports?limit=${encodeURIComponent(String(limit))}`),
  get: (jobId: string) => requestJson<{ job: BackgroundJobSummary }>(`/api/jobs/exports/${encodeURIComponent(jobId)}`),
  retry: (jobId: string) => taskAware(requestJson<{ job: BackgroundJobSummary }>(`/api/jobs/exports/${encodeURIComponent(jobId)}/retry`, jsonBody('POST', {}))),
  createRecipe: (input: {
    recipeId: string;
    versionId: string;
    exportKind: RecipeExportKind;
    format: RecipeExportFormat;
    languageProfile: ExportLanguageProfile;
    scaleRequest?: RecipeScaleRequest;
  }) => taskAware(requestJson<EnqueuedJobResponse>('/api/jobs/exports/recipe', jsonBody('POST', {
    ...input,
    idempotencyKey: crypto.randomUUID(),
    dependencyScope: 'recursive',
    mediaMode: 'none'
  }))),
  createDish: (input: {
    versionId: string;
    format: 'pdf' | 'xlsx' | 'print_html' | 'json';
    languageProfile: ExportLanguageProfile;
    versionMode: Extract<ExportVersionMode, 'published' | 'include_draft'>;
  }) => taskAware(requestJson<EnqueuedJobResponse>('/api/jobs/exports/scope', jsonBody('POST', {
    scope: { type: 'single', targets: [{ targetType: 'dish_version', targetId: input.versionId }] },
    documentPurpose: 'dish_record',
    format: input.format,
    languageProfile: input.languageProfile,
    versionMode: input.versionMode,
    idempotencyKey: crypto.randomUUID(),
    dependencyScope: 'recursive',
    mediaMode: 'none'
  }))),
  createMenu: (input: {
    menuId: string;
    versionId: string;
    format: MenuExportFormat;
    languageProfile: ExportLanguageProfile;
  }) => taskAware(requestJson<EnqueuedJobResponse>('/api/jobs/exports/menu', jsonBody('POST', {
    ...input,
    idempotencyKey: crypto.randomUUID(),
    dependencyScope: 'recursive',
    mediaMode: 'none'
  }))),
  createMenuPrep: (input: {
    menuId: string;
    versionId: string;
    format: MenuExportFormat;
    languageProfile: ExportLanguageProfile;
    serviceCount: number;
    serviceMarginPercent: number;
  }) => taskAware(requestJson<EnqueuedJobResponse>('/api/jobs/exports/menu/prep', jsonBody('POST', {
    ...input,
    idempotencyKey: crypto.randomUUID(),
    dependencyScope: 'recursive',
    mediaMode: 'none',
    versionMode: 'published'
  }))),
  createMenuBatch: (input: {
    menuId: string;
    versionId: string;
    languageProfile: ExportLanguageProfile;
    serviceCount: number;
    serviceMarginPercent: number;
  }) => taskAware(requestJson<EnqueuedJobResponse>('/api/jobs/exports/menu/batch', jsonBody('POST', {
    ...input,
    format: 'pdf',
    idempotencyKey: crypto.randomUUID(),
    dependencyScope: 'recursive',
    mediaMode: 'none',
    versionMode: 'published'
  }))),
  createMenuDossier: (input: {
    menuId: string;
    versionId: string;
    languageProfile: ExportLanguageProfile;
    serviceCount: number;
    serviceMarginPercent: number;
    includesManagementCost: boolean;
  }) => taskAware(requestJson<EnqueuedJobResponse>('/api/jobs/exports/menu/dossier', jsonBody('POST', {
    ...input,
    format: 'zip',
    idempotencyKey: crypto.randomUUID(),
    dependencyScope: 'recursive',
    mediaMode: 'none',
    versionMode: 'published'
  }))),
  createScope: (input: {
    scope: ExportScope;
    format: 'json' | 'zip';
    dependencyScope: ExportDependencyScope;
    mediaMode?: ExportMediaMode;
    versionMode: ExportVersionMode;
  }) => taskAware(requestJson<EnqueuedJobResponse>('/api/jobs/exports/scope', jsonBody('POST', {
    ...input,
    idempotencyKey: crypto.randomUUID(),
    mediaMode: input.mediaMode ?? 'none'
  }))),
  createScopePdf: (input: {
    scope: ExportScope;
    documentPurpose: ScopePdfDocumentPurpose;
    languageProfile: ExportLanguageProfile;
    versionMode: ExportVersionMode;
    format?: 'pdf' | 'xlsx';
  }) => taskAware(requestJson<EnqueuedJobResponse>('/api/jobs/exports/scope', jsonBody('POST', {
    ...input,
    format: input.format ?? 'pdf',
    idempotencyKey: crypto.randomUUID(),
    dependencyScope: 'recursive',
    mediaMode: 'none'
  }))),
  renderSnapshot: (snapshotId: string, format: RecipeExportFormat | MenuExportFormat) => taskAware(requestJson<EnqueuedJobResponse>(
    `/api/jobs/exports/snapshots/${encodeURIComponent(snapshotId)}/render`,
    jsonBody('POST', { format, idempotencyKey: crypto.randomUUID() })
  ))
};